import { z } from 'zod';

export const PROFILE_SECTION_KINDS = [
  'gallery_representation',
  'education',
  'tools',
  'studio',
  'exhibition',
  'press',
  'career',
  'other_media',
] as const;

export type ProfileSectionKind = (typeof PROFILE_SECTION_KINDS)[number];

const optionalText = z.string().optional().nullable();

/**
 * Only http(s) URLs are allowed. Blocks active schemes (javascript:, data:, vbscript:, …)
 * that would execute when a stored URL is rendered into an anchor href on the public profile.
 */
function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

const optionalUrl = z
  .string()
  .url()
  .refine(isHttpUrl, { message: 'URL must use the http or https scheme' })
  .optional()
  .nullable();

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .optional()
  .nullable();

/** Shared columns stored on ProfileSectionItem (extras live in JSONB). */
export const profileSectionItemSchema = z.object({
  title: optionalText,
  subtitle: optionalText,
  startDate: dateOnly,
  endDate: dateOnly,
  location: optionalText,
  description: optionalText,
  linkUrl: optionalUrl,
  mediaUrl: optionalUrl,
  position: z.number().int().nonnegative().optional(),
});

export type ProfileSectionItemFields = z.infer<typeof profileSectionItemSchema>;

const galleryRepresentationExtrasSchema = z.object({
  representationType: z.string().min(1),
  galleryLogoUrl: optionalUrl,
});

const educationExtrasSchema = z.object({
  distinctions: optionalText,
});

const toolsExtrasSchema = z.object({
  category: z.string().min(1),
  items: z.array(z.string().min(1)).min(1),
});

const studioExtrasSchema = z.object({
  caption: optionalText,
});

const exhibitionExtrasSchema = z.object({
  exhibitionType: z.enum(['solo', 'group']),
});

const pressExtrasSchema = z.object({
  outlet: z.string().min(1),
  headline: z.string().min(1),
  author: optionalText,
  coverImageUrl: optionalUrl,
});

const careerExtrasSchema = z.object({
  employer: optionalText,
  role: optionalText,
});

const otherMediaExtrasSchema = z.object({
  caption: optionalText,
  mediaType: optionalText,
});

export const profileSectionExtrasSchemaByKind = {
  gallery_representation: galleryRepresentationExtrasSchema,
  education: educationExtrasSchema,
  tools: toolsExtrasSchema,
  studio: studioExtrasSchema,
  exhibition: exhibitionExtrasSchema,
  press: pressExtrasSchema,
  career: careerExtrasSchema,
  other_media: otherMediaExtrasSchema,
} as const satisfies Record<ProfileSectionKind, z.ZodTypeAny>;

export type ProfileSectionExtrasByKind = {
  [K in ProfileSectionKind]: z.infer<(typeof profileSectionExtrasSchemaByKind)[K]>;
};

export type ValidatedProfileSectionItem<K extends ProfileSectionKind = ProfileSectionKind> =
  ProfileSectionItemFields & {
    kind: K;
    extras: ProfileSectionExtrasByKind[K];
  };

export class ProfileSectionValidationError extends Error {
  readonly zodError?: z.ZodError;

  constructor(message: string, zodError?: z.ZodError) {
    super(message);
    this.name = 'ProfileSectionValidationError';
    this.zodError = zodError;
  }
}

export function isProfileSectionKind(kind: string): kind is ProfileSectionKind {
  return (PROFILE_SECTION_KINDS as readonly string[]).includes(kind);
}

function parseWithKindError(label: string, schema: z.ZodTypeAny, value: unknown): unknown {
  const result = schema.safeParse(value);
  if (!result.success) {
    throw new ProfileSectionValidationError(`${label} validation failed`, result.error);
  }
  return result.data;
}

/**
 * Validates kind + common item fields + per-kind extras for create/update paths.
 */
export function validateProfileSectionItem<K extends ProfileSectionKind>(
  kind: K,
  input: unknown
): ValidatedProfileSectionItem<K> {
  if (!isProfileSectionKind(kind)) {
    throw new ProfileSectionValidationError(`Unknown profile section kind: ${kind}`);
  }

  const record = input !== null && typeof input === 'object' ? (input as Record<string, unknown>) : {};
  const { extras: rawExtras, ...commonInput } = record;

  const common = parseWithKindError(
    'Profile section item',
    profileSectionItemSchema,
    commonInput
  ) as ProfileSectionItemFields;
  const extras = parseWithKindError(
    'Profile section extras',
    profileSectionExtrasSchemaByKind[kind],
    rawExtras ?? {}
  ) as ProfileSectionExtrasByKind[K];

  return { ...common, kind, extras };
}
